import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Bell, CircleAlert, ExternalLink, Info, Key, Trash2, TriangleAlert } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { fillPlaceholders } from '@/services/core/apiError'
import {
  notificationTarget,
  type NotificationSeverity,
  type SystemNotification,
} from '@/services/notifications/notificationsApi'
import { formatDateTime } from '@/utils/format'
import { useNotifications } from '../contexts/NotificationsContext'
import { useModule } from '../contexts/ModuleContext'
import { useSystemNotifications } from '../hooks/useSystemNotifications'

const SEVERITY_ICONS: Record<NotificationSeverity, { icon: typeof Bell; className: string }> = {
  ERROR: { icon: CircleAlert, className: 'text-(--ad-danger)' },
  WARNING: { icon: TriangleAlert, className: 'text-(--ad-warning)' },
  IMPORTANT: { icon: Info, className: 'text-(--ad-primary)' },
  INFO: { icon: Bell, className: 'text-muted-foreground' },
}

/** `notification.lineItem.ended.end.date.passed` → `lineItemEndedEndDatePassed`. */
function messageKey(code: string): string {
  const [first = '', ...rest] = code.replace(/^notification\./, '').split('.')
  return first + rest.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join('')
}

function stringParams(parameters: Record<string, unknown> = {}): Record<string, string> {
  return Object.fromEntries(
    Object.entries(parameters).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(', ') : String(value ?? ''),
    ])
  )
}

function NotificationText({ notification }: { notification: SystemNotification }) {
  const { code, text } = notification.message
  const values = stringParams(notification.message.parameters)
  return (
    <Trans
      i18nKey={`notifications.messages.${messageKey(code)}`}
      values={values}
      components={{ strong: <strong className="font-medium" /> }}
      defaults={text ? fillPlaceholders(text, values) : code}
    />
  )
}

function NotificationItem({
  notification,
  unread,
  onNavigate,
}: {
  notification: SystemNotification
  unread: boolean
  onNavigate: () => void
}) {
  const { icon: SeverityIcon, className } = SEVERITY_ICONS[notification.severity] ?? SEVERITY_ICONS.INFO
  const target = notificationTarget(notification)
  const body = (
    <>
      <SeverityIcon className={cn('mt-0.5 size-4 shrink-0', className)} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm">
          <NotificationText notification={notification} />
          {target?.external && <ExternalLink className="ml-1 inline size-3 text-muted-foreground" />}
        </span>
        <span className="text-xs text-muted-foreground">{formatDateTime(notification.time)}</span>
      </span>
      {unread && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-(--ad-primary)" />}
    </>
  )
  const itemClass = 'flex items-start gap-3 rounded-md px-3 py-2.5 text-foreground'

  if (!target) return <div className={itemClass}>{body}</div>
  if (target.external) {
    return (
      <a
        className={cn(itemClass, 'hover:bg-accent')}
        href={target.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
      >
        {body}
      </a>
    )
  }
  return (
    <Link className={cn(itemClass, 'hover:bg-accent')} to={target.href} onClick={onNavigate}>
      {body}
    </Link>
  )
}

/**
 * The header's notification bell: the network's system notifications and, in
 * the political module, advertiser invites still waiting to be linked to a
 * campaign. Opening it marks the notifications read.
 */
export function NotificationsMenu() {
  const { t } = useTranslation()
  const { currentModule } = useModule()
  const { count: pendingInvites } = useNotifications()
  const { notifications, unread, isLoading, markRead, clear, canClear } = useSystemNotifications()
  const [open, setOpen] = useState(false)
  /** Unread when the menu opened; reading them empties `unread` straight away. */
  const [shownUnread, setShownUnread] = useState<Set<number>>(new Set())

  const count = pendingInvites + unread.length
  const label = count > 0 ? t('notifications.bellUnread', { count }) : t('appShell.notifications')
  const showInvites = currentModule.id === 'political' && pendingInvites > 0

  const onOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setShownUnread(new Set(unread.map((n) => n.id)))
      markRead()
    }
    setOpen(isOpen)
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button className="ad-topbar__iconbtn ad-topbar__bell" title={label} aria-label={label}>
          <Bell className="size-4" />
          {count > 0 && <span className="ad-topbar__badge">{count}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex max-h-[70vh] w-96 flex-col gap-0 p-0">
        <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
          <span className="text-sm font-semibold">{t('appShell.notifications')}</span>
          {canClear && notifications.length > 0 && (
            <Button type="button" variant="ghost" size="sm" onClick={clear}>
              <Trash2 className="size-4" />
              {t('notifications.clear')}
            </Button>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1">
          {showInvites && (
            <>
              <Link
                to="/political"
                className="flex items-start gap-3 rounded-md px-3 py-2.5 text-foreground hover:bg-accent"
                onClick={() => setOpen(false)}
              >
                <Key className="mt-0.5 size-4 shrink-0 text-(--ad-primary)" />
                <span className="text-sm">
                  {t('notifications.pendingTitle', { count: pendingInvites })}
                </span>
              </Link>
              {notifications.length > 0 && <Separator className="my-1" />}
            </>
          )}
          {notifications.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              unread={shownUnread.has(n.id)}
              onNavigate={() => setOpen(false)}
            />
          ))}
          {!showInvites && notifications.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {isLoading ? t('common.loading') : t('notifications.empty')}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
